import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, mergeMap } from 'rxjs/operators';
import { Observable, EMPTY, of } from 'rxjs';
import { ActivityValueChainActions } from './activity-value-chain.actions';
import { ActivityValueChainService } from '../../service/activity-value-chain.service';
import { DownloadActivityValueChainMutation, UpdateActivityValueChainMutation } from '../../../../graphql-planner-tool/generated';
import { MessageService } from 'primeng/api';


@Injectable()
export class ActivityValueChainEffects {

  loadValueChains$ = createEffect(
    (aS: ActivityValueChainService = inject(ActivityValueChainService)) => {
      return this.actions$.pipe(
        ofType(ActivityValueChainActions.loadValueChains),
        mergeMap(({ campaignId, parameters }) =>
          aS.getActivityValueChains(campaignId, parameters).pipe(
            map((valueChains) => {
              return ActivityValueChainActions.loadValueChainsSuccess({
                valueChains
              });
            }),
            catchError((error) => of(ActivityValueChainActions.loadValueChainsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllValueChains$ = createEffect(
    (aS: ActivityValueChainService = inject(ActivityValueChainService)) => {
      return this.actions$.pipe(
        ofType(ActivityValueChainActions.loadAllValueChains),
        mergeMap(({ campaignId, parameters }) =>
          aS.getActivityValueChains(campaignId, parameters).pipe(
            map((valueChains) => {
              return ActivityValueChainActions.loadAllValueChainsSuccess({
                valueChains
              });
            }),
            catchError((error) => of(ActivityValueChainActions.loadAllValueChainsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadCampaigns$ = createEffect(
    (aS: ActivityValueChainService = inject(ActivityValueChainService)) => {
      return this.actions$.pipe(
        ofType(ActivityValueChainActions.loadCampaigns),
        mergeMap(({ campaignType }) =>
          aS.getActivityValueChainsCampaigns(campaignType).pipe(
            map((campaigns) => {
              return ActivityValueChainActions.loadCampaignsSuccess({
                campaigns
              });
            }),
            catchError((error) => of(ActivityValueChainActions.loadCampaignsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateValueChain$ = createEffect(
    (aS: ActivityValueChainService = inject(ActivityValueChainService)) => {
      return this.actions$.pipe(
        ofType(ActivityValueChainActions.updateActivityValueChain),
        mergeMap(({ category, categoryDescription, id, macroCategory, viewOrder }) =>
          aS.updateActivityValueChain(category, id, macroCategory, categoryDescription, viewOrder).pipe(
            map((update) => {
              return ActivityValueChainActions.updateActivityValueChainSuccess({
                update: update as UpdateActivityValueChainMutation['updateValueChain']
              });
            }),
            catchError((error) => of(ActivityValueChainActions.updateActivityValueChainFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteValueChain$ = createEffect(
    (aS: ActivityValueChainService = inject(ActivityValueChainService)) => {
      return this.actions$.pipe(
        ofType(ActivityValueChainActions.deleteActivityValueChain),
        mergeMap(({ id, campaignId, parameters }) =>
          aS.deleteValueChain(id).pipe(
            mergeMap(() =>
              of(
                ActivityValueChainActions.loadValueChains({ campaignId, parameters })
              )
            ),
            catchError(error =>
              of(ActivityValueChainActions.deleteActivityValueChainFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  createValueChain$ = createEffect(
    (aS: ActivityValueChainService = inject(ActivityValueChainService)) => {
      return this.actions$.pipe(
        ofType(ActivityValueChainActions.createActivityValueChain),
        mergeMap(({ campaignId, category, categoryDescription, macroCategory, parameters }) =>
          aS.createValueChain(category, campaignId, macroCategory, categoryDescription).pipe(
            mergeMap(() =>
              of(
                ActivityValueChainActions.loadValueChains({ campaignId, parameters })
              )
            ),
            catchError(error =>
              of(ActivityValueChainActions.createActivityValueChainFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  downloadActivityValueChain$ = createEffect(
    (aS: ActivityValueChainService = inject(ActivityValueChainService)) => {
      return this.actions$.pipe(
        ofType(ActivityValueChainActions.downloadActivityValueChain),
        mergeMap(({ campaignId, parameters }) =>
          aS.downloadActivityValueChain(campaignId, parameters).pipe(
            map((download) => {
              if (download?.data) {
                const f = download.data;
                const byteChars = atob(f.content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                  byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = window.document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = f.filename ?? 'activity-value-chain.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
              }
              return ActivityValueChainActions.downloadActivityValueChainSuccess({ download });
            }),
            catchError((error) => of(ActivityValueChainActions.downloadActivityValueChainFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private readonly actions$: Actions, private readonly messageService: MessageService) { }
}
